import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HealingIcon from '@mui/icons-material/Healing';
import { useTranslation } from 'react-i18next';
import api from '../api/axiosConfig';
import { getMediaBaseUrl } from '../utils/mediaUrl';
import { canModifyRecord } from '../utils/permissions';

const getAvatarUrl = (userId) => {
  return `${getMediaBaseUrl()}/users/${userId}.png?${new Date().getTime()}`;
};

const MedicalTab = ({
  playerId,
  onDataLoaded,
  playerType = '',
  onRefreshLatestMedical
}) => {
  const { t } = useTranslation();
  const [medicalData, setMedicalData] = useState([]);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentUser] = useState(() => JSON.parse(localStorage.getItem('user') || 'null'));

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const typeLower = (playerType || '').toLowerCase();
  const isPro = typeLower === 'pro';
  const isCantera = typeLower === 'cantera';

  // États pour l'édition
  const [currentMedical, setCurrentMedical] = useState(null);
  const [editedDate, setEditedDate] = useState('');
  const [editedFuente, setEditedFuente] = useState('');
  const [editedLibelle, setEditedLibelle] = useState('');
  const [editedChirurgie, setEditedChirurgie] = useState('');
  const [editedRecuperation, setEditedRecuperation] = useState('');
  const [editedAfficheLogoBlessure, setEditedAfficheLogoBlessure] = useState(false);

  // État pour la suppression
  const [medicalToDelete, setMedicalToDelete] = useState(null);

  useEffect(() => {
    if (!playerId) return;
    
    const fetchMedicalData = async () => {
      try {
        const response = await api.get('/api/medical', {
          params: { playerId }
        });

        setMedicalData(response.data || []);
        if (onDataLoaded) {
          onDataLoaded(response.data && response.data.length > 0);
        }
      } catch (error) {
        console.error(t('medical.errors.fetchError'), error);
      }
    };

    fetchMedicalData();
  }, [playerId, onDataLoaded, t]);

  const handleEditClick = (medicalRow) => {
    setCurrentMedical(medicalRow);
    const mainDateStr = medicalRow.date
      ? new Date(medicalRow.date).toISOString().split('T')[0]
      : '';
    setEditedDate(mainDateStr);
    setEditedFuente(medicalRow.fuente || '');
    setEditedLibelle(medicalRow.libelle || '');
    setEditedChirurgie(medicalRow.chirurgie || '');
    setEditedRecuperation(medicalRow.recuperation || '');
    setEditedAfficheLogoBlessure(Boolean(medicalRow.affiche_logo_blessure));
    setOpenEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!currentMedical) return;

    const updateData = {
      date: editedDate,
      idnuser: currentMedical.idnuser,  // Conserver l'utilisateur original
      id_player: currentMedical.id_player || playerId,  // Conserver l'id du joueur
      fuente: editedFuente,
      libelle: editedLibelle,
      chirurgie: editedChirurgie,
      recuperation: editedRecuperation,
      affiche_logo_blessure: isPro ? (editedAfficheLogoBlessure ? 1 : 0) : 0
    };

    try {
      await api.put(`/api/medical/${currentMedical.id}`, updateData);

      setMedicalData((prevData) =>
        prevData.map((item) =>
          item.id === currentMedical.id
            ? { ...item, ...updateData }
            : item
        )
      );

      setOpenEditDialog(false);
      setCurrentMedical(null);
      
      if (onRefreshLatestMedical) {
        onRefreshLatestMedical();
      }
    } catch (error) {
      console.error(t('medical.errors.updateError'), error);
      alert(t('medical.errors.updateError'));
    }
  };

  const handleDeleteClick = (medicalRow) => {
    setMedicalToDelete(medicalRow);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!medicalToDelete) return;

    try {
      await api.delete(`/api/medical/${medicalToDelete.id}`);

      setMedicalData((prevData) =>
        prevData.filter((item) => item.id !== medicalToDelete.id)
      );

      setOpenDeleteDialog(false);
      setMedicalToDelete(null);
      
      if (onRefreshLatestMedical) {
        onRefreshLatestMedical();
      }
    } catch (error) {
      console.error(t('medical.errors.deleteError'), error);
      alert(t('medical.errors.deleteError'));
    }
  };

  if (!medicalData.length) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="h6">{t('medical.noData')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        {t('medical.historyTitle')}
      </Typography>
      
      <TableContainer component={Paper}>
        <Table size={isMobile ? "small" : "medium"}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600 }}>{t('medical.createdBy')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('medical.date')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('medical.injury')}</TableCell>
              {isPro && !isMobile && <TableCell sx={{ fontWeight: 600 }}>{t('medical.source')}</TableCell>}
              <TableCell sx={{ fontWeight: 600 }}>{t('medical.surgery')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('medical.recovery')}</TableCell>
              {isCantera && <TableCell sx={{ fontWeight: 600 }}>{t('medical.returnDate')}</TableCell>}
              <TableCell sx={{ fontWeight: 600 }} align="center">{t('medical.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {medicalData.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar
                      src={getAvatarUrl(row.idnuser)}
                      alt={row.usuario_nombre || 'Usuario'}
                      sx={{ width: 24, height: 24 }}
                    />
                    <Typography variant="caption">
                      {row.usuario_nombre || 'Sin nombre'}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  {new Date(row.date).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {row.affiche_logo_blessure === 1 && (
                      <HealingIcon sx={{ color: 'red', fontSize: 20 }} />
                    )}
                    <Typography variant="body2">
                      {row.libelle || '-'}
                    </Typography>
                  </Box>
                </TableCell>
                {isPro && !isMobile && (
                  <TableCell>{row.fuente || '-'}</TableCell>
                )}
                <TableCell>
                  <Chip
                    label={row.chirurgie === 'si' ? t('medical.surgeryOptions.yes') : row.chirurgie === 'no' ? t('medical.surgeryOptions.no') : t('medical.surgeryOptions.none')}
                    size="small"
                    color={row.chirurgie === 'si' ? 'error' : 'default'}
                    variant={row.chirurgie === 'si' ? 'filled' : 'outlined'}
                  />
                </TableCell>
                <TableCell>
                  {row.recuperation || '-'}
                  {isCantera && row.recuperation && /^\d+$/.test(row.recuperation) && ` ${t('medical.days')}`}
                </TableCell>
                {isCantera && (
                  <TableCell>
                    {row.date_reprise ? new Date(row.date_reprise).toLocaleDateString() : '-'}
                  </TableCell>
                )}
                <TableCell align="center">
                  {canModifyRecord(currentUser, row.usuario_id) && (
                    <Box>
                      <IconButton
                        size="small"
                        onClick={() => handleEditClick(row)}
                        sx={{ color: '#1976d2' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(row)}
                        sx={{ color: '#d32f2f' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog para editar */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{t('medical.editDialog.title')}</DialogTitle>
        <DialogContent>
          {currentMedical && (
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label={t('medical.editDialog.dateLabel')}
                type="date"
                value={editedDate}
                onChange={(e) => setEditedDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />

              {isPro && (
                <TextField
                  fullWidth
                  label={t('medical.editDialog.sourceLabel')}
                  value={editedFuente}
                  onChange={(e) => setEditedFuente(e.target.value)}
                  sx={{ mb: 2 }}
                />
              )}

              <TextField
                fullWidth
                label={t('medical.editDialog.injuryLabel')}
                value={editedLibelle}
                onChange={(e) => setEditedLibelle(e.target.value)}
                sx={{ mb: 2 }}
              />

              <TextField
                select
                fullWidth
                label={t('medical.editDialog.surgeryLabel')}
                value={editedChirurgie}
                onChange={(e) => setEditedChirurgie(e.target.value)}
                sx={{ mb: 2 }}
              >
                <MenuItem value="">{t('medical.surgeryOptions.none')}</MenuItem>
                <MenuItem value="no">{t('medical.surgeryOptions.no')}</MenuItem>
                <MenuItem value="si">{t('medical.surgeryOptions.yes')}</MenuItem>
              </TextField>
              
              {isPro ? (
                <TextField
                  select
                  fullWidth
                  label={t('medical.editDialog.recoveryLabel')}
                  value={editedRecuperation}
                  onChange={(e) => setEditedRecuperation(e.target.value)}
                  sx={{ mb: 2 }}
                >
                  <MenuItem value="">{t('medical.recoveryOptions.select')}</MenuItem>
                  <MenuItem value="menos de una semana">{t('medical.recoveryOptions.lessThanWeek')}</MenuItem>
                  <MenuItem value="2 semanas">{t('medical.recoveryOptions.twoWeeks')}</MenuItem>
                  <MenuItem value="3 semanas">{t('medical.recoveryOptions.threeWeeks')}</MenuItem>
                  <MenuItem value="4 semanas">{t('medical.recoveryOptions.fourWeeks')}</MenuItem>
                  <MenuItem value="1-2 meses">{t('medical.recoveryOptions.oneToTwoMonths')}</MenuItem>
                  <MenuItem value="2-3 meses">{t('medical.recoveryOptions.twoToThreeMonths')}</MenuItem>
                  <MenuItem value="4-5 meses">{t('medical.recoveryOptions.fourToFiveMonths')}</MenuItem>
                  <MenuItem value="5-6 meses">{t('medical.recoveryOptions.fiveToSixMonths')}</MenuItem>
                  <MenuItem value="6 meses - 1 año">{t('medical.recoveryOptions.sixMonthsToYear')}</MenuItem>
                  <MenuItem value="más de 1 año">{t('medical.recoveryOptions.moreThanYear')}</MenuItem>
                </TextField>
              ) : (
                isCantera && (
                  <TextField
                    fullWidth
                    label={t('medical.editDialog.recoveryDaysLabel')}
                    type="number"
                    value={editedRecuperation}
                    onChange={(e) => setEditedRecuperation(e.target.value)}
                    InputProps={{ inputProps: { min: 0 } }}
                    sx={{ mb: 2 }}
                  />
                )
              )}
              
              {isPro && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={editedAfficheLogoBlessure}
                      onChange={(e) => setEditedAfficheLogoBlessure(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      {t('medical.editDialog.showLogoLabel')} <HealingIcon sx={{ ml: 1 }} />
                    </span>
                  }
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>{t('medical.editDialog.cancel')}</Button>
          <Button onClick={handleSaveEdit} variant="contained" color="primary">
            {t('medical.editDialog.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para confirmar eliminación */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>{t('medical.deleteDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('medical.deleteDialog.message')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>{t('medical.deleteDialog.cancel')}</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            {t('medical.deleteDialog.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MedicalTab;